import '../css/app.css';

import { createInertiaApp, router } from '@inertiajs/vue3';
import { resolvePageComponent } from 'laravel-vite-plugin/inertia-helpers';
import type { DefineComponent } from 'vue';
import { createApp, h } from 'vue';
import { initializeTheme } from './composables/useAppearance';
import { route } from './utils/route'; // Import route helper
import { getCsrfToken } from './utils/csrf';
import axios from 'axios';

const appName = import.meta.env.VITE_APP_NAME || 'Laravel';

// Configure axios to include CSRF token
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

const csrfToken = getCsrfToken();
if (csrfToken) {
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;
}

// Axios interceptor to refresh CSRF token on each request
axios.interceptors.request.use((config) => {
    const token = getCsrfToken();
    if (token) {
        config.headers['X-CSRF-TOKEN'] = token;
    }
    return config;
}, (error) => {
    return Promise.reject(error);
});

// Keep CSRF token in sync after every Inertia page navigation
router.on('navigate', (event) => {
    const page = event.detail.page;
    const csrf = page.props.csrf_token as string;
    if (csrf) {
        let meta = document.head.querySelector('meta[name="csrf-token"]');
        if (meta) {
            meta.setAttribute('content', csrf);
        }
        axios.defaults.headers.common['X-CSRF-TOKEN'] = csrf;
    }
});

createInertiaApp({
    title: (title) => (title ? `${title} - ${appName}` : appName),
    resolve: (name) => resolvePageComponent(`./pages/${name}.vue`, import.meta.glob<DefineComponent>('./pages/**/*.vue')),
    setup({ el, App, props, plugin }) {
        const app = createApp({ render: () => h(App, props) });
        
        // Make route function available globally in Vue components
        app.config.globalProperties.route = route;
        app.provide('route', route);
        
        // Also attach to window for global access
        if (typeof window !== 'undefined') {
            (window as any).route = route;
        }
        
        app.use(plugin).mount(el);
    },
    progress: {
        color: '#0a4068', // AUAS brand color
        // Disable progress bar during authentication to prevent state conflicts
        includeCSS: true,
        showSpinner: false,
    },
});

// This will set light / dark mode on page load...
initializeTheme();
